<script lang="ts">
    import {_} from 'svelte-i18n'
    import type {Conversation, Message, Session} from './types';
    import {DefinedMessageType, MessageType, Role} from './types';
    import MessageInputResult from './MessageInput.svelte';
    import {v4} from 'uuid';
    import {onDestroy} from 'svelte';
    import ConversationMessages from './ConversationMessages.svelte';
    import {activeOperators, conversations, definedMessageService, messages, mySession, soundPlayer} from './services';
    import type {Readable} from 'svelte/store';
    import {derived} from 'svelte/store';
    import {hasAnyRole, isOfSession, isPending, isStartable, isStarted, wasEnded} from './service/util';
    import LoadingDiv from './LoadingDiv.svelte';
    import ConversationMessageInput from './ConversationMessageInput.svelte';
    import DefinedMessage from './DefinedMessage.svelte';
    import PulseButton from './PulseButton.svelte';
    import {ConversationStartedNotifier} from './service/conversation-started-notifier';
    import ConversationHeader from './ConversationHeader.svelte';

    export let session: Session;
    export let conversation: Conversation;


    const conversationMessages: Readable<Message[]> = derived(messages, (_messages: Message[]) => {
        if (_messages) {
            return _messages.filter(message => message.conversationId === conversation.id);
        }
        return [];
    });


    const clientPristineActiveConversationNotification: ConversationStartedNotifier = new ConversationStartedNotifier(mySession, conversations, soundPlayer);
    const conversationMessagesUnsubscribe = conversationMessages.subscribe(_messages => {
        if (_messages.some(message => message.authorId === session.id)) {
            clientPristineActiveConversationNotification.snoozeAll();
        }
    });
    onDestroy(conversationMessagesUnsubscribe);


    const operatorWelcomeDefinedMessage: Readable<string> = definedMessageService.createStoreForPrimary(DefinedMessageType.OPERATOR_CHAT_MESSAGE);

    const predefinedMessage = derived([conversationMessages, operatorWelcomeDefinedMessage], ([_conversationMessages, _operatorWelcomeDefinedMessage]: [Message[], string]) => {
        if (session.roles.includes(Role.OPERATOR) && _conversationMessages.length === 0 && _operatorWelcomeDefinedMessage.length > 0) {
            return _operatorWelcomeDefinedMessage;
        }
        return '';
    });

    function addMessage(newMessageEvent: CustomEvent<MessageInputResult>) {
        const message: Message = {
            id: v4(),
            conversationId: conversation.id,
            authorId: session.id,
            type: conversation.client.id === session.id ? MessageType.STANDARD_FROM_CLIENT : MessageType.STANDARD_FROM_OPERATOR,
            text: newMessageEvent.detail.text,
            createDate: new Date()
        };
        messages.addMessage(message);
    }
</script>

<div style="height: 100%; width: 100%; display: grid; grid-template-columns: 1fr; grid-template-rows: auto 1fr auto;">

    <ConversationHeader session={session}
                        conversation={conversation}
                        on:conversation-start
                        on:conversation-close
                        on:conversation-end
                        on:conversation-pause
                        on:conversation-unpause />

    {#if $activeOperators.length === 0}
        <div style="display: flex; flex-direction: column; align-items: center; text-align: center; justify-content: center; margin: 30px 0">
            <div class="mdc-typography--headline4" style="margin: 5px 0; padding: 5px 0">{$_('user.session.ConsultantLoggedOutFromChat')}</div>
            <div class="mdc-typography--body1" style="max-width: 800px; text-align: left;">
                <DefinedMessage type={DefinedMessageType.CLIENT_WAITING_ROOM_NO_OPERATOR} />
            </div>
        </div>
    {:else if session.roles.includes(Role.CLIENT) && isPending(conversation)}
        <div style="display: flex; flex-direction: column; align-items: center; text-align: center; justify-content: center; margin: 30px 0">
            <div class="mdc-typography--headline4" style="margin: 5px 0; padding: 5px 0">{$_('chat.WaitForChatStart')}</div>
            <div class="mdc-typography--body1" style="max-width: 800px;">
                <DefinedMessage type={DefinedMessageType.CLIENT_WAITING_ROOM_INFO} />
            </div>
            <LoadingDiv design="calm" />
        </div>
    {:else if $clientPristineActiveConversationNotification}
        <div style="display: flex; flex-direction: column; align-items: center; text-align: center; justify-content: center; margin: 30px 0">
            <div class="mdc-typography--headline4" style="margin: 5px 0; padding: 5px 0">{$_('user.session.ConsultantJoined')}</div>
            <div class="mdc-typography--body1" style="max-width: 800px;">
                {$_('chat.NowYouCanStartTyping')}
            </div>
            <PulseButton title={$_('commons.OkITakeNote')} on:click={() => clientPristineActiveConversationNotification.snoozeAll()}>OK</PulseButton>
        </div>
    {/if}

    {#if hasAnyRole(session, Role.OPERATOR, Role.INTERVISOR, Role.ADMIN) || isStarted(conversation) || wasEnded(conversation) || $conversationMessages.length > 0}
        <ConversationMessages session={session} conversation={conversation} messages={$conversationMessages} />
    {/if}

    {#if (isOfSession(conversation, session) && isStarted(conversation)) || (isPending(conversation) && isStartable(conversation, session))}
        <div style="margin: 4px;">
            <ConversationMessageInput session={session} conversation={conversation} on:message-add={addMessage} value={$predefinedMessage} />
        </div>
    {/if}

</div>